import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import './style.scss';
import Box from '@mui/material/Box';
import ListButton from 'component/ListButton';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import JitsiMeetJS from 'store/modules/base/lib-jitsi-meet/_';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { CloseIcon } from 'assets/images';
import { translate } from 'store/modules/base/i18n';
import { connect } from 'store/modules/base/redux';
import { submitDeviceSelectionTab } from 'store/modules/device-selection';
import { getAudioOutputDeviceId } from 'store/modules/base/devices/functions';
import { isIosMobileBrowser } from 'store/modules/base/environment/utils';

function Settings(props) {
  const [camera, setCamera] = React.useState(props.selectedVideoInputId);
  const [microphone, setMicrophone] = React.useState(
    props.selectedAudioInputId
  );
  const [speaker, setSpeaker] = React.useState(props.selectedAudioOutputId);
  const [open, setOpen] = React.useState(0);

  useEffect(() => {
    if (props.selectedVideoInputId) setCamera(props.selectedVideoInputId);
  }, [props.selectedVideoInputId]);
  useEffect(() => {
    if (props.selectedAudioOutputId) setSpeaker(props.selectedAudioOutputId);
  }, [props.selectedAudioOutputId]);
  useEffect(() => {
    if (props.selectedAudioInputId) setMicrophone(props.selectedAudioInputId);
  }, [props.selectedAudioInputId]);

  const handleClose = () => {
    setOpen(0);
  };
  const handleOpen = (id) => {
    setOpen(id);
  };
  function _renderSelector(_deviceSelectorProps) {
    const _onSelect = (e) => {
      const deviceId = e.target.value;

      if (_deviceSelectorProps.selectedDeviceId !== deviceId) {
        _deviceSelectorProps.onSelect(deviceId);
      }
    };

    return (
      <Fragment key={_deviceSelectorProps.id}>
        <Box className="selectText" htmlFor={_deviceSelectorProps.id}>
          {props.t(_deviceSelectorProps.label)}
        </Box>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className="dialogSelect"
            id="demo-controlled-open-select"
            labelId="demo-controlled-open-select-label"
            onChange={_onSelect}
            onClose={handleClose}
            onOpen={() => handleOpen(_deviceSelectorProps.id)}
            open={open === _deviceSelectorProps.id}
            value={
              _deviceSelectorProps.id === 'audioInput'
                ? microphone
                : _deviceSelectorProps.id === 'videoInput'
                ? camera
                : speaker
            }>
            {_deviceSelectorProps.devices.map((device) => (
              <MenuItem key={device.deviceId} value={device.deviceId}>
                {device.label || device.deviceId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Fragment>
    );
  }

  function _renderSelectors() {
    const { availableDevices, hasAudioPermission, hasVideoPermission } = props;

    const configurations = [
      {
        devices: availableDevices.audioInput,
        hasPermission: hasAudioPermission,
        icon: 'icon-microphone',
        isDisabled: props.disableAudioInputChange || props.disableDeviceChange,
        key: 'audioInput',
        id: 'audioInput',
        label: 'settings.selectMic',
        onSelect: (selectedAudioInputId) => setMicrophone(selectedAudioInputId),
        selectedDeviceId: props.selectedAudioInputId
      },
      {
        devices: availableDevices.videoInput,
        hasPermission: hasVideoPermission,
        icon: 'icon-camera',
        isDisabled: props.disableVideoInputSelect || props.disableDeviceChange,
        key: 'videoInput',
        id: 'videoInput',
        label: 'settings.selectCamera',
        onSelect: (selectedVideoInputId) => setCamera(selectedVideoInputId),
        selectedDeviceId: props.selectedVideoInputId
      }
    ];

    if (!props.hideAudioOutputSelect) {
      configurations.push({
        devices: availableDevices.audioOutput,
        hasPermission: hasAudioPermission || hasVideoPermission,
        icon: 'icon-speaker',
        isDisabled: props.disableDeviceChange,
        key: 'audioOutput',
        id: 'audioOutput',
        label: 'settings.selectAudioOutput',
        onSelect: (selectedAudioOutputId) => setSpeaker(selectedAudioOutputId),
        selectedDeviceId: props.selectedAudioOutputId
      });
    }

    return configurations.map((config) => _renderSelector(config));
  }
  return (
    <React.Fragment>
      <Dialog open={props.open} onClose={props.onClose} className="dialogView">
        <DialogTitle>
          <Box className="menuTitle" onClick={props.onClose}>
            <Box>Settings</Box>
            <CloseIcon />
          </Box>
        </DialogTitle>
        <DialogContent className="divider" dividers={true}>
          {_renderSelectors()}
        </DialogContent>
        <DialogActions>
          <ListButton gray onClick={props.onClose}>
            Close
          </ListButton>
          <ListButton
            big
            onClick={() => {
              props.updateDevices({
                selectedVideoInputId: camera,
                selectedAudioInputId: microphone,
                selectedAudioOutputId: speaker
              });
              props.onClose();
            }}>
            Save
          </ListButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
const mapStateToProps = (state) => {
  const disablePreviews = isIosMobileBrowser();

  const settings = state['features/base/settings'];
  const { permissions, availableDevices } = state['features/base/devices'];
  const inputDeviceChangeSupported =
    JitsiMeetJS.mediaDevices.isDeviceChangeAvailable('input');
  const speakerChangeSupported =
    JitsiMeetJS.mediaDevices.isDeviceChangeAvailable('output');

  // When the previews are disabled we don't need multiple audio input support in order to change the mic. This is the
  // case for Safari on iOS.
  const disableAudioInputChange =
    !JitsiMeetJS.mediaDevices.isMultipleAudioInputSupported() &&
    !(disablePreviews && inputDeviceChangeSupported);
  const disableVideoInputSelect = !inputDeviceChangeSupported;
  const selectedAudioInputId =
    settings.userSelectedMicDeviceId ?? settings.micDeviceId;
  const selectedAudioOutputId =
    settings.userSelectedAudioOutputDeviceId ?? getAudioOutputDeviceId();
  const selectedVideoInputId =
    settings.userSelectedCameraDeviceId ?? settings.cameraDeviceId;
  // audio input change will be a problem only when we are in a
  // conference and this is not supported, when we open device selection on
  // welcome page changing input devices will not be a problem
  // on welcome page we also show only what we have saved as user selected devices
  // if (isDisplayedOnWelcomePage) {
  //     disableAudioInputChange = false;
  //     disableVideoInputSelect = false;
  //     selectedAudioInputId = userSelectedMic;
  //     selectedAudioOutputId = getUserSelectedOutputDeviceId(state);
  //     selectedVideoInputId = userSelectedCamera;
  // }

  // we fill the device selection dialog with the devices that are currently
  // used or if none are currently used with what we have in settings(user selected)
  return {
    availableDevices,
    disableAudioInputChange,
    disableDeviceChange: !JitsiMeetJS.mediaDevices.isDeviceChangeAvailable(),
    disableVideoInputSelect,
    hasAudioPermission: permissions.audio,
    hasVideoPermission: permissions.video,
    hideAudioInputPreview:
      disableAudioInputChange ||
      !JitsiMeetJS.isCollectingLocalStats() ||
      disablePreviews,
    hideAudioOutputPreview: !speakerChangeSupported || disablePreviews,
    hideAudioOutputSelect: !speakerChangeSupported,
    hideVideoInputPreview: !inputDeviceChangeSupported || disablePreviews,
    selectedAudioInputId,
    selectedAudioOutputId,
    selectedVideoInputId
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateDevices: (state) => dispatch(submitDeviceSelectionTab(state, false))
  };
};

export default React.memo(
  translate(
    connect(mapStateToProps, mapDispatchToProps)(withBoundary(Settings))
  )
);

Settings.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  t: PropTypes.func,
  selectedVideoInputId: PropTypes.string,
  selectedAudioInputId: PropTypes.string,
  selectedAudioOutputId: PropTypes.string,
  hideAudioOutputPreview: PropTypes.bool,
  hideAudioInputPreview: PropTypes.bool,
  hideVideoInputPreview: PropTypes.bool,
  hideAudioOutputSelect: PropTypes.bool,
  hasAudioPermission: PropTypes.bool,
  hasVideoPermission: PropTypes.bool,
  disableVideoInputSelect: PropTypes.bool,
  disableDeviceChange: PropTypes.bool,
  disableAudioInputChange: PropTypes.bool,
  availableDevices: PropTypes.any,
  updateDevices: PropTypes.func
};
